import { useEffect, useMemo } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BlogAuthorProfile from '@/pages/blog/components/BlogAuthorProfile';
import BlogRelatedArticles from '@/pages/blog/components/BlogRelatedArticles';
import BlogCTA from '@/pages/blog/components/BlogCTA';
import { blogArticles } from '@/mocks/blogData';
import PageHead from '@/components/seo/PageHead';
import { useLanguageContext } from '@/i18n/LanguageContext';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang } = useLanguageContext();

  const article = useMemo(() => {
    return blogArticles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return article.relatedSlugs
      .map((rs) => blogArticles.find((a) => a.slug === rs))
      .filter(Boolean) as typeof blogArticles;
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Not found
  if (!article) {
    return (
      <>
        <PageHead
          title="記事が見つかりません"
          description="お探しの記事は削除されたか、URLが変更された可能性があります。日本執事アカデミー講師ブログの最新記事一覧はこちらからご覧いただけます。"
          path={`/blog/${slug ?? ''}`}
          noindex
          lang={lang}
        />
        <Header />
        <main className="min-h-[60vh] flex items-center justify-center pt-24">
          <div className="text-center px-8">
            <h1 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-4">
              記事が見つかりません
            </h1>
            <p className="text-[16px] text-foreground-500 tracking-[0.04em] mb-8">
              お探しの記事は削除されたか、URLが変更された可能性があります。
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 border border-accent-500/50 text-accent-500 hover:text-accent-400 hover:border-accent-400 text-[15px] tracking-[0.08em] transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>ブログ一覧に戻る</span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      {/* 記事ごとの動的メタ(title/description/canonical)。excerptは70-140字で生成時検証済みのためdescriptionにそのまま使用 */}
      <PageHead
        title={article.title}
        description={article.excerpt}
        path={`/blog/${article.slug}`}
        keywords={article.keywords.join(', ')}
        ogImage={article.imageUrl}
        lang={lang}
        citationAuthor={article.author}
      />
      {/* Structured Data: Article + FAQPage + BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Article',
                headline: article.title,
                description: article.excerpt,
                author: {
                  '@type': 'Person',
                  name: article.author,
                },
                datePublished: article.date,
                dateModified: article.updated,
                publisher: {
                  '@type': 'Organization',
                  name: '一般社団法人全日本執事協会',
                },
                image: article.imageUrl,
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://butler-academy.jp/blog/${article.slug}`,
                },
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'ホーム',
                    item: 'https://butler-academy.jp/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: '講師ブログ',
                    item: 'https://butler-academy.jp/blog',
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                    item: `https://butler-academy.jp/blog/${article.slug}`,
                  },
                ],
              },
              {
                '@type': 'FAQPage',
                mainEntity: article.faq.map((item) => ({
                  '@type': 'Question',
                  name: item.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                  },
                })),
              },
            ],
          }),
        }}
      />

      <Header />

      <main className="min-h-screen">
        {/* Article Header */}
        <article className="pt-36 pb-0 md:pt-44">
          <div className="max-w-[800px] mx-auto px-8">

            {/* Category + Date */}
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[12px] tracking-[0.08em] text-accent-600 bg-accent-100/70 px-2.5 py-1 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-[14px] text-foreground-500 tracking-[0.04em]">{article.date}</span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] leading-snug mb-5">
              {article.title}
            </h1>

            {/* Definition Text (AEO対応) */}
            <div className="border-l-2 border-accent-500/40 pl-5 py-1 mb-8">
              <p className="text-[16px] text-foreground-700 leading-relaxed tracking-[0.03em]">
                <strong className="font-medium text-foreground-900">{article.definitionText}</strong>
              </p>
            </div>

            <Breadcrumb items={[{ label: '講師ブログ', labelEn: 'Instructor Blog', to: '/blog' }, { label: article.title }]} />

            {/* Featured Image */}
            <div className="w-full h-[320px] md:h-[400px] mb-10 overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Table of Contents */}
            <nav className="bg-background-100 border border-background-200/60 p-6 mb-10" aria-label="目次">
              <h2 className="font-heading text-[17px] text-foreground-900 tracking-[0.06em] mb-4">
                目次
              </h2>
              <ol className="space-y-2">
                {article.toc.map((item, idx) => (
                  <li key={item.id} className="flex items-start gap-2">
                    <span className="text-[14px] text-accent-600 font-medium mt-[3px]">{idx + 1}.</span>
                    <a
                      href={`#${item.id}`}
                      className="text-[15px] text-foreground-700 hover:text-accent-600 transition-colors tracking-[0.03em] leading-relaxed"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Article Body */}
            <div className="prose-custom mb-12">
              {article.body.map((block, idx) => {
                if (block.type === 'h2') {
                  const tocItem = article.toc.find((t) => block.content.includes(t.text) || t.text.includes(block.content.slice(0, 10)));
                  const id = tocItem?.id || `section-${idx}`;
                  return (
                    <h2
                      key={idx}
                      id={id}
                      className="font-heading text-lg md:text-xl text-foreground-900 tracking-[0.06em] mt-10 mb-4 pb-2 border-b border-background-200/50"
                    >
                      {block.content}
                    </h2>
                  );
                }
                if (block.type === 'h3') {
                  return (
                    <h3
                      key={idx}
                      className="font-heading text-base text-foreground-800 tracking-[0.05em] mt-8 mb-3"
                    >
                      {block.content}
                    </h3>
                  );
                }
                if (block.type === 'p') {
                  return (
                    <p
                      key={idx}
                      className="text-[16px] text-foreground-700 leading-[2] tracking-[0.03em] mb-4"
                    >
                      {block.content}
                    </p>
                  );
                }
                if (block.type === 'list' && block.items) {
                  return (
                    <ul key={idx} className="list-disc pl-5 mb-5 space-y-2">
                      {block.items.map((item, iIdx) => (
                        <li
                          key={iIdx}
                          className="text-[16px] text-foreground-700 leading-relaxed tracking-[0.03em]"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            {/* Author Profile */}
            <BlogAuthorProfile author={article.author} role={article.authorRole} />

            {/* FAQ Section (AEO対応) */}
            {article.faq.length > 0 && (
              <section className="mt-12 mb-12 border-t border-background-200/60 pt-10">
                <h2 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-6">
                  よくある質問
                </h2>
                <div className="space-y-4">
                  {article.faq.map((item, idx) => (
                    <div key={idx} className="border border-background-200/60 p-5">
                      <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2">
                        Q. {item.question}
                      </h3>
                      <p className="text-[15px] text-foreground-700 leading-relaxed tracking-[0.03em]">
                        {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        {/* Related Articles */}
        <BlogRelatedArticles articles={relatedArticles} />

        {/* CTA */}
        <BlogCTA ctaType={article.ctaType} />

        {/* Last Updated */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[800px] mx-auto px-8 text-center">
            <p className="text-[13px] text-foreground-500 tracking-[0.05em]">
              最終更新: {article.updated}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}